import axios from 'axios';

import { constants } from '../../Helpers/constantsFile';
import { bearer, businessUrl } from '../settings/settingsApi';

/**
 * Switching the public menu on and off.
 *
 * ── The endpoint lives next door ─────────────────────────────────────────
 *
 * `PATCH /api/client/v1/business`, imported from the settings feature rather
 * than restated here. Both screens write to the same business — this one a
 * single switch, that one everything else — and one URL in one file is the
 * only way they cannot drift apart when it moves.
 *
 * The server takes the business from the token, so there is no id to pass and
 * no way to aim this at someone else's restaurant.
 */

/**
 * Turn the public menu on or off.
 *
 * Sends only `publicMenu`. Never `publicBusinessId` — the server owns that; it
 * is created the first time the menu is switched on, never changes afterwards,
 * and every printed code depends on it staying exactly as it is.
 *
 * @returns the updated business, so the caller can put it straight into the
 *          session rather than re-fetching. On the first switch-on it carries
 *          the newly created `publicBusinessId`.
 */
export async function setPublicMenu({ on, token }) {
  const { data } = await axios.patch(
    businessUrl(),
    { publicMenu: Boolean(on) },
    { headers: { authorization: bearer(token) }, timeout: 20000 }
  );
  return data?.data?.updatedBusiness || null;
}

/**
 * Why it did not work, in the two flavours that need different words.
 *
 *   refused  the server will not let this account do it — the route is
 *            managers only. Nothing the restaurant can retry.
 *   failed   anything else. Worth trying again.
 */
export function classifyToggleError(error) {
  const status = error?.response?.status;
  if (status === 403 || status === 401 || status === 404) return 'refused';
  return 'failed';
}

/**
 * Read the public menu exactly as a customer's phone will.
 *
 * Used by the panel's "test" check, so a manager can see the code works before
 * putting it on the tables. No token: this is the public endpoint, and sending
 * an Authorization header here would test something a customer never does.
 */
export async function fetchPublicMenu(publicBusinessId) {
  const { data } = await axios.get(
    `${constants.baseUrl}/public/menu/${publicBusinessId}`,
    { timeout: 20000 }
  );
  return data?.data || null;
}
